// resources/js/routes.js
//
// Every route component is lazy-loaded (dynamic import) so that visiting one page
// does NOT download the entire application — the home page ships only the shared
// shell + Home, not admin/dashboard/business-detail(mapbox)/etc.
//
// EXCEPTION: Home is imported EAGERLY. It is the landing page, and lazy-loading it
// left the router-view empty for a beat after mount (Layout's Header + Footer render
// first), which slammed the footer up under the header and back down — a ~0.9 CLS
// spike. Bundling Home into the main entry makes Layout + Home paint together (no
// gap) and renders the LCP <h1> as soon as app.js executes.
import Home from './components/Home.vue';

const routes = [
    // website routes
    { path: '/', component: Home, name: 'home' },
    { path: '/test', component: () => import('./components/about/Test'), name: 'test' },
    { path: '/about', component: () => import('./components/about/About.vue') },
    { path: '/contact', component: () => import('./components/about/Contact.vue') },
    { path: '/user/login', component: () => import('./components/auth/Login') },
    { path: '/user/signup', component: () => import('./components/auth/Signup') },
    { path: '/user/reset-password', component: () => import('./components/auth/Email.vue') },
    { path: '/pricing', component: () => import('./components/Pricing') },
    { path: '/cart', component: () => import('./components/cart/Cart.vue') },
    { path: '/add-business/:string', name: 'AddBusiness', component: () => import('./components/Add_Business') },
    { path: '/favorite', name: 'favorite', component: () => import('./components/Favorite.vue'), meta: { requiresAuth: true } },
    { path: '/about-us', name: 'about', component: () => import('./components/about/About.vue') },
    { path: '/contact-us', name: 'contact', component: () => import('./components/about/Contact.vue') },
    { path: '/blogs', name: 'Blog', component: () => import('./components/about/Blog.vue') },
    { path: '/blog/:slug', name: 'BlogsDetails', component: () => import('./components/about/Blogs-Details.vue') },
    { path: '/faqs', name: 'faqs', component: () => import('./components/about/Faq.vue') },
    { path: '/help-center', name: 'helpCenter', component: () => import('./components/about/Help-center.vue') },
    { path: '/terms-and-conditions', name: 'teamsCondition', component: () => import('./components/about/TeamsCondition.vue') },
    { path: '/cookies-policy', name: 'cookiePolicy', component: () => import('./components/about/CookiePolicy.vue') },
    { path: '/privacy-policy', name: 'privacyPolicy', component: () => import('./components/about/PrivacyPolicy.vue') },
    { path: '/referral-program', name: 'referralProgram', component: () => import('./components/about/ReferralProgram.vue') },
    { path: '/get-started', name: 'learnMore', component: () => import('./components/about/Learnmore.vue') },
    { path: '/company-info', name: 'companyInfo', component: () => import('./components/about/company-info.vue') },
    { path: '/investor-info', name: 'investors', component: () => import('./components/about/investors.vue') },
    { path: '/check-out', name: 'checkout', component: () => import('./components/Cart/Checkout.vue'), meta: { requiresAuth: true } },
    { path: '/order-success', name: 'OrderSuccess', component: () => import('./components/Cart/OrderSuccess.vue'), meta: { requiresAuth: true } },

    // dashboard
    { path: '/change-password', name: 'changePassword', component: () => import('./components/business_dashboard/include/Change-password.vue'), meta: { requiresAuth: true } },
    { path: '/review', name: 'review', component: () => import('./components/business_dashboard/include/Review.vue'), meta: { requiresAuth: true } },
    { path: '/order', name: 'order', component: () => import('./components/business_dashboard/include/Order.vue'), meta: { requiresAuth: true } },
    { path: '/view_order/:id', name: 'orderProduct', component: () => import('./components/business_dashboard/include/Order-product.vue'), meta: { requiresAuth: true } },

    {
        path: '/category/:category?/:sub_category?/:country?/:city?',
        name: 'category',
        component: () => import('./components/Business-list.vue'),
        props: true,
    },
    {
        path: '/search/all',
        name: 'search-all',
        component: () => import('./components/AllSearchResults.vue'),
    },
    {
        path: '/search/services',
        name: 'search-services',
        component: () => import('./components/service/ServiceSearchResults.vue'),
    },
    {
        path: '/search/products',
        name: 'search-products',
        component: () => import('./components/product/ProductSearchResults.vue'),
    },
    {
        path: '/single-product/:id',
        name: 'single-product',
        component: () => import('./components/product/ProductPage.vue'),
        props: true, // Pass route params as props to the component
    },
    {
        path: '/service/:slug',
        name: 'service-page',
        component: () => import('./components/service/ServicePage.vue'),
        props: true,
    },

    {
        path: '/:slug',
        name: 'single-business',
        component: () => import('./components/business/Business_Main'),
        props: true,
    },

    // business dashboard routes
    {
        path: '/dashboard-2',
        name: 'business-dashboard-2',
        component: () => import('./components/business_dashboard/Dashboard_2'),
    },
    {
        path: '/dashboard',
        name: 'business-dashboard',
        component: () => import('./components/business_dashboard/Dashboard'),
        meta: { requiresAuth: true },
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import('./components/business_dashboard/Profile'),
    },
    {
        path: '/support',
        name: 'support',
        component: () => import('./components/business_dashboard/support-ticket/Support.vue'),
    },
    {
        path: '/add_support',
        name: 'add_support',
        component: () => import('./components/business_dashboard/support-ticket/add_support.vue'),
    },
    {
        path: '/view_support/:id',
        name: 'view_support',
        component: () => import('./components/business_dashboard/support-ticket/view_support.vue'),
    },
    // business claim
    {
        path: '/claim_business',
        name: 'claim_business',
        component: () => import('./components/Claim_Business.vue'),
    },
    // write a review
    {
        path: '/write_review',
        name: 'write_review',
        component: () => import('./components/write_review.vue'),
        meta: { requiresAuth: true },
    },

    // Explore Business Page
    {
        path: '/business-explore',
        name: 'business_landing',
        component: () => import('./components/business/business_author_landing/Landing.vue'),
    },
    {
        path: '/author-explore',
        name: 'author_landing',
        component: () => import('./components/business/business_author_landing/Landing.vue'),
    },

    // Dynamic pages route
    {
        path: '/pages/:slug',
        name: 'more_pages',
        component: () => import('./components/about/MorePages.vue'),
        props: true,
    },
];

export default routes;
